import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import api from '../common/api';
import BuyInfoCenterModal from '../facility-number/BuyInfoCenterModal';
import { useFacilityAnisList, type FacilityAniEditable } from '../hooks/FacilityAni';
import { useAlerts } from '../alert/AlertContext';

const NAME_OPTIONS = ['Main', 'Fax', 'Inmate Voicemail'] as const;

type FacilityAnisSectionProps = {
  facility: { id: number };
  onBusyChange?: (busy: boolean) => void;
};

type AniRow = FacilityAniEditable & { _client?: string };

function rowKey(row: AniRow): string {
  return row.id != null ? `id:${row.id}` : `c:${row._client ?? ''}`;
}

const FacilityAnisSection: React.FC<FacilityAnisSectionProps> = ({ facility, onBusyChange }) => {
  const nameListId = useId();
  const alerts = useAlerts();
  const clientSeq = useRef(0);

  const {
    execute: refetch,
    result: listFromApi,
    loading,
    error: listError,
  } = useFacilityAnisList(facility.id);

  const [rows, setRows] = useState<AniRow[]>([]);
  const [editing, setEditing] = useState<Set<string>>(() => new Set());
  const [saving, setSaving] = useState<Set<string>>(() => new Set());
  const [buyInfoOpen, setBuyInfoOpen] = useState(false);
  const [buyInfoBasis, setBuyInfoBasis] = useState<FacilityAniEditable | null>(null);

  useEffect(() => {
    if (listFromApi != null) {
      setRows(listFromApi);
      setEditing(new Set());
      setSaving(new Set());
    }
  }, [listFromApi]);

  useEffect(() => {
    if (listError != null) {
      alerts.error('An error occurred while loading ANIs');
    }
  }, [listError]);

  const aniBusy = loading || saving.size > 0;
  useEffect(() => {
    onBusyChange?.(aniBusy);
  }, [aniBusy, onBusyChange]);

  const addAni = useCallback(() => {
    const row: AniRow = {
      id: undefined,
      ani: '',
      name: '',
      extension: '',
      _client: `${++clientSeq.current}`,
    };
    setRows((r) => [row, ...r]);
    setEditing((e) => new Set(e).add(rowKey(row)));
  }, []);

  const startEdit = useCallback((row: AniRow) => {
    setEditing((e) => new Set(e).add(rowKey(row)));
  }, []);

  const setField = useCallback((k: string, field: 'ani' | 'name' | 'extension', value: string) => {
    setRows((prev) => prev.map((r) => (rowKey(r) === k ? { ...r, [field]: value } : r)));
  }, []);

  const saveRow = useCallback(
    (row: AniRow) => {
      const k = rowKey(row);
      if (row.ani.trim() === '' || row.name.trim() === '') {
        alerts.error('ANI and Name are required');
        return;
      }
      const payload: Record<string, unknown> = {
        ani: row.ani.trim(),
        name: row.name.trim(),
        extension: row.extension.trim(),
        facilityId: facility.id,
      };
      if (row.id != null) {
        payload.id = row.id;
      }

      setSaving((s) => new Set(s).add(k));

      const done = () => {
        setSaving((s) => {
          const n = new Set(s);
          n.delete(k);
          return n;
        });
      };

      const p =
        row.id != null
          ? api.put(`/facility-ani/${row.id}`, payload)
          : api.post('/facility-ani', payload);

      void p.then(
        () => {
          alerts.success('The ANI was saved');
          setEditing((e) => {
            const n = new Set(e);
            n.delete(k);
            return n;
          });
          void refetch(facility.id);
          done();
        },
        () => {
          alerts.error('An error occurred while saving the ANI');
          done();
        }
      );
    },
    [facility.id, refetch]
  );

  const onBuyInfoCenter = useCallback(() => {
    const basis = rows.length > 0 ? rows[0] : null;
    setBuyInfoBasis(basis);
    setBuyInfoOpen(true);
  }, [rows]);

  const onBuyInfoModalClose = useCallback(
    (_saved: boolean) => {
      setBuyInfoOpen(false);
      setBuyInfoBasis(null);
      void refetch(facility.id);
    },
    [facility.id, refetch]
  );

  const hasInfoCenter = rows.some((r) => r.name === 'Info Center');

  return (
    <>
      <Button
        type="button"
        variant="primary"
        className="btn-labeled mb-2 me-2"
        disabled={loading}
        onClick={addAni}>
        <span className="btn-label">
          <i className="fas fa-plus-circle" />{' '}
        </span>
        Add Inbound Number
      </Button>
      {!hasInfoCenter && (
        <Button
          type="button"
          variant="primary"
          className="btn-labeled mb-2"
          disabled={loading}
          onClick={onBuyInfoCenter}>
          <span className="btn-label">
            <i className="fas fa-plus-circle" />{' '}
          </span>
          Buy Info Center Number
        </Button>
      )}
      {loading && rows.length === 0 && <p className="text-muted">Loading…</p>}
      {rows.length > 0 && (
        <table className="table table-striped table-hover table-bordered">
          <thead>
            <tr>
              <th>ANI</th>
              <th>Name</th>
              <th>Extension</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const k = rowKey(row);
              const edit = editing.has(k);
              const sav = saving.has(k);
              return (
                <tr key={k}>
                  <td>
                    <input
                      className="form-control"
                      value={row.ani}
                      disabled={!edit || sav}
                      required
                      onChange={(e) => setField(k, 'ani', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="form-control"
                      type="text"
                      list={nameListId}
                      value={row.name}
                      disabled={!edit || sav}
                      required
                      onChange={(e) => setField(k, 'name', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="form-control"
                      type="text"
                      value={row.extension}
                      disabled={!edit || sav}
                      onChange={(e) => setField(k, 'extension', e.target.value)}
                    />
                  </td>
                  <td>
                    {edit && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={sav}
                        onClick={() => saveRow(row)}>
                        {sav ? 'Saving…' : 'Save'}
                      </Button>
                    )}
                    {!edit && (
                      <Button type="button" variant="secondary" size="sm" onClick={() => startEdit(row)}>
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <datalist id={nameListId}>
        {NAME_OPTIONS.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
      {!loading && rows.length === 0 && <p className="text-muted">No inbound numbers.</p>}
      <BuyInfoCenterModal
        show={buyInfoOpen}
        facility={facility}
        basisNumber={buyInfoBasis}
        onClose={onBuyInfoModalClose}
      />
    </>
  );
};

export default FacilityAnisSection;
